// Form pieces for a blueprint, shared by the New dialog and the settings
// tabs (and, for BlueprintField, the workflow run form and the New job
// form): a labelled field with either a tooltip (`hint`, operator feedback
// 2026-09-07: no paragraphs) or one short visible line under the label
// (`help`: what to type, read without a tap on a phone — a field that
// explains what to type uses `help`, never `hint`), toggle chips for
// agents, a filterable checklist for skills and knowledge libraries (a
// chosen value it does not know yet says "Loading…" while the options
// load, never "Not found": SD7), the capability switches and the
// time-limit dropdown.

import { useId, useMemo, useState, type ReactNode } from "react";
import { Check, Search, Users } from "lucide-react";

import type { SubagentCapabilities } from "@/api/subagentBlueprints";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { cx } from "@/components/ui/controlStyles";
import { Dropdown } from "@/components/ui/Dropdown";
import { InfoTip } from "@/components/ui/InfoTip";
import { Input } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";

import { CAPABILITIES, capabilityOn, timeLimitOptions, withCapability } from "./blueprintModel";
import { capabilityConsequence, type IntegrationRef } from "./effectiveModel";

export { InfoTip };

/**
 * Label (+ tooltip, + visible help line) over a control. `children` gets
 * the id for the control so the <label> points at it; a `group` (chips, a
 * checklist) has no single control, so its heading is plain text the group
 * names itself by (`aria-labelledby={labelId}`). With `help`, `helpId` is
 * the help line's id, for the control's `aria-describedby`.
 */
export function BlueprintField({
  label,
  hint,
  help,
  aside,
  error,
  group,
  children,
  className,
}: {
  label: string;
  /** A tooltip on an (i) beside the label. */
  hint?: string;
  /** One short line shown under the label: what to type. */
  help?: ReactNode;
  aside?: ReactNode;
  error?: string | null;
  group?: boolean;
  children: (ids: { id: string; labelId: string; helpId?: string }) => ReactNode;
  className?: string;
}) {
  const id = useId();
  const labelId = `${id}-label`;
  const helpId = help ? `${id}-help` : undefined;
  const Label = group ? "span" : "label";
  return (
    <div className={cx("flex min-w-0 flex-col gap-1.5", className)}>
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex min-h-[20px] items-center gap-1.5">
          <Label id={labelId} htmlFor={group ? undefined : id} className="text-sm font-medium text-fg">
            {label}
          </Label>
          {hint && <InfoTip text={hint} />}
          {aside && <span className="ml-auto text-xs text-fg-subtle">{aside}</span>}
        </div>
        {help && (
          <p id={helpId} className="text-xs text-fg-muted">
            {help}
          </p>
        )}
      </div>
      {children({ id, labelId, helpId })}
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export interface ChipOption {
  value: string;
  label: string;
}

const CHIP_OFF = "shadow-[inset_0_0_0_1px_var(--c-border-visible)] text-fg-muted";

function Chip({ on, title, onClick, disabled, children }: { on: boolean; title?: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      shape="pill"
      pressed={on}
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={cx("text-sm", !on && CHIP_OFF)}
    >
      {on && <Check aria-hidden className="-ml-0.5 h-3.5 w-3.5" strokeWidth={2.25} />}
      {children}
    </Button>
  );
}

/**
 * One toggle chip per option. With `allLabel`, a first chip stands for
 * every option (the value is then ["*"], which also covers agents added
 * later).
 */
export function ChipToggles({
  options,
  value,
  onChange,
  labelledBy,
  allLabel,
  emptyText,
  disabled,
}: {
  options: ChipOption[];
  value: string[];
  onChange: (next: string[]) => void;
  labelledBy: string;
  allLabel?: string;
  emptyText: string;
  disabled?: boolean;
}) {
  const all = !!allLabel && value.includes("*");
  // Names the server still holds that are not an option any more (a
  // renamed or deleted agent) stay visible, so a save does not drop them silently.
  const unknown = value.filter((v) => v !== "*" && !options.some((o) => o.value === v));
  const toggle = (v: string) => {
    // Leaving "all": everyone but this one.
    if (all) return onChange(options.map((o) => o.value).filter((x) => x !== v));
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };
  if (!options.length && !allLabel && !unknown.length) {
    return <p className="text-sm text-fg-subtle">{emptyText}</p>;
  }
  return (
    <div role="group" aria-labelledby={labelledBy} className="flex flex-wrap items-center gap-1.5">
      {allLabel && (
        <Chip on={all} disabled={disabled} title={all ? "Choose one by one instead" : "Everyone, including agents added later"} onClick={() => onChange(all ? [] : ["*"])}>
          <Users aria-hidden className="h-3.5 w-3.5" strokeWidth={1.75} />
          {allLabel}
        </Chip>
      )}
      {/* Under "all" the agents are covered by that chip, not chosen one by one: shown unpressed, and a click starts choosing. */}
      {options.map((o) => (
        <Chip
          key={o.value}
          on={!all && value.includes(o.value)}
          disabled={disabled}
          title={all ? `Included via ${allLabel}; click to choose one by one without ${o.label}` : undefined}
          onClick={() => toggle(o.value)}
        >
          {o.label}
        </Chip>
      ))}
      {all && options.length > 0 && <span className="text-xs text-fg-subtle">(all via {allLabel})</span>}
      {unknown.map((v) => (
        <Chip key={v} on disabled={disabled} title="Not in this workspace any more: click to drop it" onClick={() => toggle(v)}>
          {v}
        </Chip>
      ))}
    </div>
  );
}

const LOADING = "Loading…";

export interface CheckOption {
  value: string;
  label: string;
  hint?: string;
}

/**
 * A filterable list of checkboxes; chosen ones sort first. `locked` values
 * show ticked and cannot be unticked (included whatever is saved: the
 * library of a subagent's old person project), their `lockedHint` as the tooltip. While the
 * options load (`loading`, or no options yet while something is chosen), a
 * chosen value the list does not know yet shows a spinner and "Loading…",
 * never "Not found in this workspace", which is only said once the options
 * are in.
 */
export function CheckList({
  options,
  value,
  onChange,
  labelledBy,
  emptyText,
  filterLabel,
  className,
  locked = [],
  lockedHint,
  loading,
}: {
  options: CheckOption[];
  value: string[];
  onChange: (next: string[]) => void;
  labelledBy: string;
  emptyText: string;
  filterLabel: string;
  className?: string;
  locked?: readonly string[];
  lockedHint?: string;
  /** The options are still loading (default: none yet while values are chosen). */
  loading?: boolean;
}) {
  const [filter, setFilter] = useState("");
  const pending = loading ?? (options.length === 0 && value.length > 0);
  const rows = useMemo(() => {
    const known = new Set(options.map((o) => o.value));
    const extra = value.filter((v) => !known.has(v)).map((v) => ({ value: v, label: v, hint: pending ? LOADING : "Not found in this workspace" }));
    const q = filter.trim().toLowerCase();
    return [...extra, ...options]
      .filter((o) => !q || o.label.toLowerCase().includes(q) || o.value.includes(q) || (o.hint ?? "").toLowerCase().includes(q))
      .sort((a, b) => Number(isOn(b.value)) - Number(isOn(a.value)) || a.label.localeCompare(b.label));
    function isOn(v: string) {
      return value.includes(v) || locked.includes(v);
    }
  }, [options, value, filter, locked, pending]);

  if (!options.length && !value.length) return <p className="text-sm text-fg-subtle">{emptyText}</p>;
  const toggle = (v: string, on: boolean) => onChange(on ? [...value, v] : value.filter((x) => x !== v));
  return (
    <div className={cx("flex min-w-0 flex-col gap-2", className)}>
      {options.length > 8 && (
        <div className="relative">
          <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter" aria-label={filterLabel} fieldSize="sm" className="pl-8" />
        </div>
      )}
      <div
        role="group"
        aria-labelledby={labelledBy}
        className="max-h-[240px] overflow-y-auto rounded-control bg-inset px-3 py-2 shadow-[inset_0_0_0_1px_var(--c-border-subtle)]"
      >
        {rows.length === 0 ? (
          <p className="py-1 text-sm text-fg-subtle">Nothing matches.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.map((o) => {
              const fixed = locked.includes(o.value);
              return (
                <li key={o.value} title={(fixed ? lockedHint : o.hint) || undefined}>
                  <Checkbox
                    checked={fixed || value.includes(o.value)}
                    disabled={fixed}
                    onChange={(on) => toggle(o.value, on)}
                    label={
                      <span className="flex min-w-0 flex-col">
                        <span className="line-clamp-1">{o.label}</span>
                        {(fixed ? lockedHint : o.hint) && (
                          <span className="flex items-center gap-1 text-xs text-fg-subtle">
                            {!fixed && o.hint === LOADING && <Spinner className="h-3 w-3" />}
                            <span className="line-clamp-1">{fixed ? lockedHint : o.hint}</span>
                          </span>
                        )}
                      </span>
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * The four capability checkboxes (applied on Save); each hint is a tooltip,
 * and what the draft setting means shows as one line under its switch, only
 * when there is something to say (effectiveModel.capabilityConsequence).
 */
export function CapabilityToggles({
  value,
  onChange,
  labelledBy,
  integrations,
}: {
  value: SubagentCapabilities | undefined;
  onChange: (next: SubagentCapabilities) => void;
  labelledBy: string;
  /** What is scoped to it (the view's effective.integrations); unknown before it exists. */
  integrations?: readonly IntegrationRef[];
}) {
  return (
    <div role="group" aria-labelledby={labelledBy} className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {CAPABILITIES.map((c) => {
        const line = capabilityConsequence(c.key, value, integrations);
        return (
          <div key={c.key} className="flex min-w-0 flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <Checkbox checked={capabilityOn(value, c.key)} onChange={(on) => onChange(withCapability(value, c.key, on))} label={c.label} />
              <InfoTip text={c.hint} />
            </div>
            {line && <p className={cx("pl-7 text-xs", line.tone === "warning" ? "text-warning" : "text-fg-subtle")}>{line.text}</p>}
          </div>
        );
      })}
    </div>
  );
}

export function TimeLimitDropdown({
  minutes,
  onChange,
  id,
  labelledBy,
  max,
  min,
}: {
  minutes: number;
  onChange: (m: number) => void;
  id?: string;
  labelledBy?: string;
  /** The longest choice offered (a blueprint's: the workspace's time per run). */
  max?: number | null;
  /** The shortest preset offered. */
  min?: number;
}) {
  return (
    <Dropdown
      id={id}
      ariaLabel="Time limit per run"
      labelledBy={labelledBy}
      value={String(minutes)}
      options={timeLimitOptions(minutes, max, min)}
      onChange={(v) => onChange(Number(v))}
      minWidth={140}
      matchTriggerWidth={false}
      className="self-start"
    />
  );
}
